/**
 * Authentication Interceptors
 *
 * Axios request/response interceptors for automatic token management:
 * - Adds Authorization header with access token to all requests
 * - Handles 401 errors with automatic token refresh via shared module
 */

import type { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '../stores/useAuthStore';
import { refreshAccessToken, ensureValidToken } from '@/lib/token-refresh';

// Extended config to track retry attempts
interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
  /**
   * Opt-in flag: allow this request to be replayed after a 401-triggered
   * token refresh even though its HTTP method is not inherently safe. Mirrors
   * `ApiRequestOptions.idempotent` in `lib/api-client.ts` — default is "do not
   * replay" for mutating methods (Story 04-01 / D3).
   */
  idempotent?: boolean;
}

/** HTTP methods that are safe to blindly replay after a 401 token refresh. */
const SAFE_RETRY_METHODS = new Set(['get', 'head', 'options']);

/**
 * Whether a request may be replayed after a 401-triggered token refresh:
 * safe methods always may; mutating methods only when the caller explicitly
 * opted in via `idempotent: true` on the request config.
 */
function canReplayAfterRefresh(config: ExtendedAxiosRequestConfig): boolean {
  const method = (config.method ?? 'get').toLowerCase();
  return SAFE_RETRY_METHODS.has(method) || config.idempotent === true;
}

/**
 * Setup request interceptor to add Authorization header
 */
export function setupRequestInterceptor(instance: AxiosInstance): void {
  instance.interceptors.request.use(
    async (config) => {
      // Ensure token is valid before making request (handles returning from inactivity)
      await ensureValidToken();

      const { accessToken } = useAuthStore.getState();
      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }
      return config;
    },
    (error: unknown) => Promise.reject(error instanceof Error ? error : new Error(String(error)))
  );
}

/**
 * Setup response interceptor to handle 401 errors with token refresh
 */
export function setupResponseInterceptor(instance: AxiosInstance): void {
  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as ExtendedAxiosRequestConfig | undefined;

      // Only handle 401 errors with valid config
      if (error.response?.status !== 401 || !originalRequest) {
        return Promise.reject(error);
      }

      // Prevent infinite loops
      if (originalRequest._retry) {
        useAuthStore.getState().logout();
        return Promise.reject(error);
      }

      // Opt-in, not opt-out: a non-idempotent mutating request is never
      // replayed after a 401 — surface it to the caller instead (D3).
      if (!canReplayAfterRefresh(originalRequest)) {
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      try {
        // Refresh token (handles concurrent requests internally)
        const newToken = await refreshAccessToken();

        // Update header for retry
        originalRequest.headers.Authorization = `Bearer ${newToken}`;

        // Retry original request
        return await instance(originalRequest);
      } catch {
        // Already handled by shared module (logout called)
        return Promise.reject(error);
      }
    }
  );
}
